import { useEffect, useState } from "react";

import { listLocations } from "../utils/api";
import {
    LatLng,
    LocationQueryResponse,
    isoLatitude,
    isoLongitude,
    isoMiles,
} from "../utils/models";

const QUERY_RADIUS = isoMiles.wrap(200);

const QUERY_LIMIT = 26;

type UserLocation = {
    detected: LatLng | null;
    entered: LatLng | null;
};

const getURLGeoLocation = (): LatLng | null => {
    if (!window.location.search) {
        return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const lat = parseFloat(urlParams.get("latitude") || "");
    const lng = parseFloat(urlParams.get("longitude") || "");
    if (isNaN(lat) || isNaN(lng)) {
        return null;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
    }
    return {
        lat: isoLatitude.wrap(lat),
        lng: isoLongitude.wrap(lng),
    };
};

export const useUserLocation = () => {
    const [userLocation, setUserLocation] = useState<UserLocation>(() => ({
        detected: typeof window !== "undefined" ? getURLGeoLocation() : null,
        entered: null,
    }));

    useEffect(() => {
        if (userLocation.detected) return;
        const onSuccess: PositionCallback = (pos) => {
            setUserLocation((s) => ({
                ...s,
                detected: {
                    lat: isoLatitude.wrap(pos.coords.latitude),
                    lng: isoLongitude.wrap(pos.coords.longitude),
                },
            }));
        };
        const onError: PositionErrorCallback = (err) => {
            console.error(err);
        };
        navigator.geolocation.getCurrentPosition(onSuccess, onError, {
            enableHighAccuracy: false,
        });
    }, [userLocation.detected]);

    const setEnteredLocation = (enteredLocation: LatLng | null): void => {
        setUserLocation((s) => ({
            ...s,
            entered: enteredLocation,
        }));
    };

    return {
        userLocation,
        setEnteredLocation,
    };
};

export const useNearbyRetailLocations = (userLocation: UserLocation) => {
    const [queryResp, setQueryResp] = useState<LocationQueryResponse | null>(
        null,
    );

    const updateLocations = async (): Promise<void> => {
        const center = userLocation.entered || userLocation.detected;
        if (!center) {
            return;
        }
        const resp = await listLocations({
            center: center,
            radius: QUERY_RADIUS,
            limit: QUERY_LIMIT,
        });
        setQueryResp(resp);
    };

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- async data fetch; setState is in the .then callback, not synchronous
        updateLocations().catch((err) => {
            console.error(err);
        });
    }, [userLocation]);

    return queryResp;
};
